import type { MarkushDrawingDocument, MarkushFragment, MarkushScaffold } from './markushDocument.ts';
import { isPlaceholderPosition } from './markushDocument.ts';
import { elementSymbol } from '../chemistry/elements.ts';

// #652 (Markush M3c): read-only summaries for the substituent table -- a member's condensed text
// and how many concrete structures a Markush drawing stands for.

/** Common-valence table for the elements substituents are made of; enough to count implicit
 *  hydrogens for display. Anything else shows its bare symbol. */
const DEFAULT_VALENCE: Readonly<Record<number, number>> = {
  1: 1,
  5: 3,
  6: 4,
  7: 3,
  8: 2,
  9: 1,
  14: 4,
  15: 3,
  16: 2,
  17: 1,
  35: 1,
  53: 1
};

/** Pnictogens/chalcogens gain valence with a positive charge (N+ -> 4) and lose it with a
 *  negative one (O- -> 1); the others lose valence either way. */
const CHARGE_RAISES_VALENCE = new Set([7, 8, 15, 16]);

function implicitHydrogens(structure: MarkushScaffold, atomId: string, extraBonds: number): number {
  const atom = structure.atoms[atomId];
  if (!atom) return 0;
  const base = DEFAULT_VALENCE[atom.atomicNumber];
  if (base === undefined || atom.atomicNumber === 1) return 0;
  const charge = structure.formalCharges[atomId] ?? 0;
  const valence = CHARGE_RAISES_VALENCE.has(atom.atomicNumber) ? base + charge : base - Math.abs(charge);
  const bondOrders = structure.graph.edges.reduce(
    (sum, edge) =>
      edge.source === atomId || edge.target === atomId ? sum + (structure.bonds[edge.id]?.order ?? 1) : sum,
    0
  );
  return Math.max(0, valence - bondOrders - extraBonds);
}

const SUBSCRIPT = '₀₁₂₃₄₅₆₇₈₉';

function withCount(symbol: string, count: number): string {
  if (count === 0) return '';
  return count === 1 ? symbol : symbol + [...String(count)].map((digit) => SUBSCRIPT[Number(digit)]).join('');
}

/**
 * A member's condensed text, attachment atom first, the way substituents are written in a claim:
 * `–H`, `–OH`, `–OCH₃`, `–NH₂`, `–CH₃`, `–C₂H₅`, `–CF₃`, `–CN`, `–NO₂`, `–CO₂H`, `–C₆H₅`. A
 * carbon attachment folds the rest's carbons and hydrogens into it (ethyl is `C₂H₅`, not `CH₂CH₃`);
 * otherwise the attachment atom (with its own H) leads, then carbons, other elements
 * alphabetically, and hydrogens last. Charges are left out (nitro reads `NO₂`).
 */
export function fragmentLabel(fragment: MarkushFragment): string {
  const attachment = fragment.openValenceAtomId;
  const attachmentAtom = fragment.atoms[attachment];
  if (!attachmentAtom) return '–?';
  const counts = new Map<number, number>();
  let hydrogens = 0;
  for (const node of fragment.graph.nodes) {
    const atom = fragment.atoms[node.id];
    if (!atom || node.id === attachment) continue;
    counts.set(atom.atomicNumber, (counts.get(atom.atomicNumber) ?? 0) + 1);
    hydrogens += implicitHydrogens(fragment, node.id, 0);
  }
  const attachmentHydrogens = implicitHydrogens(fragment, attachment, 1);
  const others = [...counts.entries()]
    .filter(([atomicNumber]) => atomicNumber !== 6 && atomicNumber !== 1)
    .map(([atomicNumber, count]) => [elementSymbol(atomicNumber), count] as const)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([symbol, count]) => withCount(symbol, count))
    .join('');
  const explicitHydrogens = counts.get(1) ?? 0;
  const carbons = counts.get(6) ?? 0;
  if (attachmentAtom.atomicNumber === 6) {
    return `–${withCount('C', 1 + carbons)}${others}${withCount('H', attachmentHydrogens + hydrogens + explicitHydrogens)}`;
  }
  const lead = elementSymbol(attachmentAtom.atomicNumber) + withCount('H', attachmentHydrogens);
  return `–${lead}${withCount('C', carbons)}${others}${withCount('H', hydrogens + explicitHydrogens)}`;
}

/** #658: the owner's hard cap on concrete structures per Markush search. The server's
 *  `peruselab.search.markush.max-structures` is authoritative; this mirrors its default so the
 *  client can say why before sending anything. */
export const MARKUSH_SEARCH_MAX_STRUCTURES = 250;

export type MarkushStructureCount =
  | { readonly kind: 'count'; readonly value: number }
  /** Uses a generic class or an open-ended repeat (matched by rule, #660); `value` is the count of
   *  its *finite* combinations, which is what the search cap applies to. */
  | { readonly kind: 'generic'; readonly value: number }
  /** Some label is used but not yet defined. */
  | { readonly kind: 'incomplete' };

/** Ring atoms that can carry the attachment-point substituent: those with a hydrogen to replace. */
function freeRingPositions(document: MarkushDrawingDocument, ringAtomIds: readonly string[]): number {
  return ringAtomIds.filter((id) => implicitHydrogens(document.scaffold, id, 0) > 0).length;
}

/**
 * How many concrete structures a Markush drawing stands for: the product, over its variable
 * positions, of each position's choices -- an R-group's member count, an attachment point's member
 * count times its ring's free positions, an atom list's element count, a repeat unit's count
 * range. Informational for now; M4 sets the authoring-time cap.
 */
export function markushStructureCount(document: MarkushDrawingDocument): MarkushStructureCount {
  let value = 1;
  let generic = false;
  for (const position of document.variablePositions) {
    if (isPlaceholderPosition(position) && position.kind !== 'atom-list') {
      const definition = document.rGroups[position.rGroupLabel];
      if (!definition) return { kind: 'incomplete' };
      if (definition.kind === 'homology') {
        generic = true;
        continue;
      }
      const sites = position.kind === 'attachment-point' ? freeRingPositions(document, position.ringAtomIds) : 1;
      value *= definition.members.length * sites;
    } else if (position.kind === 'atom-list') {
      value *= new Set(position.allowedAtomicNumbers).size;
    } else if (position.countMax === undefined) {
      generic = true;
    } else {
      value *= Math.max(0, position.countMax - position.countMin + 1);
    }
  }
  return generic ? { kind: 'generic', value } : { kind: 'count', value };
}

/** #658: one position's choice in a Markush match, as the server reports it. */
export interface MarkushChoiceRef {
  readonly position: number;
  readonly member?: number | null;
  readonly ringAtomId?: string | null;
  readonly atomicNumber?: number | null;
  readonly count?: number | null;
  /** #660: the group a generic class matched in this molecule, as a formula (`C2H5`). */
  readonly matchedGroup?: string | null;
}

function subscriptDigits(formula: string): string {
  return formula.replace(/\d/g, (digit) => SUBSCRIPT[Number(digit)]!);
}

/**
 * #658: "which structure matched" for a result card -- `R1 = F · [C,N,O,S] = N · n = 2`. An
 * attachment point also says where on the ring (`R1 = Cl at ring atom 3 of 6`, counting in the
 * ring's own order). Positions the server didn't report are left out.
 */
export function describeMarkushChoices(document: MarkushDrawingDocument, choices: readonly MarkushChoiceRef[]): string {
  return choices
    .flatMap((choice) => {
      const position = document.variablePositions[choice.position];
      if (!position) return [];
      if (position.kind === 'repeat-unit') return choice.count == null ? [] : [`n = ${choice.count}`];
      if (position.kind === 'atom-list') {
        if (choice.atomicNumber == null) return [];
        return [
          `[${position.allowedAtomicNumbers.map(elementSymbol).join(',')}] = ${elementSymbol(choice.atomicNumber)}`
        ];
      }
      const definition = document.rGroups[position.rGroupLabel];
      if (definition?.kind === 'homology') {
        // #660: `R1 = alkyl (C₂H₅)` -- the class, and the group it matched in this molecule.
        const group = choice.matchedGroup ? ` (${subscriptDigits(choice.matchedGroup)})` : '';
        return [`${position.rGroupLabel} = ${definition.class}${group}`];
      }
      const member =
        definition?.kind === 'enumerated' && choice.member != null ? definition.members[choice.member] : undefined;
      if (!member) return [];
      const text = `${position.rGroupLabel} = ${fragmentLabel(member).replace(/^–/, '')}`;
      if (position.kind !== 'attachment-point' || !choice.ringAtomId) return [text];
      const ringIndex = position.ringAtomIds.indexOf(choice.ringAtomId);
      return [`${text} at ring atom ${ringIndex + 1} of ${position.ringAtomIds.length}`];
    })
    .join(' · ');
}

/**
 * #839 -- a canonical key for a fragment, so the same substituent is recognised however it was
 * added (its atom ids differ every time). Iterated neighbourhood labels (Weisfeiler-Lehman),
 * rooted at the attachment atom: element, isotope, charge, and the attachment mark, refined by
 * bond orders. Distinguishes isomers such as n-propyl and isopropyl; stereo is not considered.
 */
export function fragmentKey(fragment: MarkushFragment): string {
  const nodes = fragment.graph.nodes.map((node) => node.id);
  const neighbours = new Map<string, { id: string; order: number }[]>(nodes.map((id) => [id, []]));
  for (const edge of fragment.graph.edges) {
    const order = fragment.bonds[edge.id]?.order ?? 1;
    neighbours.get(edge.source)?.push({ id: edge.target, order });
    neighbours.get(edge.target)?.push({ id: edge.source, order });
  }
  let labels = new Map(
    nodes.map((id) => {
      const atom = fragment.atoms[id] as { atomicNumber?: number; isotope?: number } | undefined;
      const charge = fragment.formalCharges[id] ?? 0;
      return [
        id,
        `${atom?.atomicNumber ?? 0}:${atom?.isotope ?? ''}:${charge}:${id === fragment.openValenceAtomId ? '*' : ''}`
      ];
    })
  );
  for (let round = 0; round < nodes.length; round++) {
    labels = new Map(
      nodes.map((id) => [
        id,
        `${labels.get(id)}(${(neighbours.get(id) ?? [])
          .map((neighbour) => `${neighbour.order}${labels.get(neighbour.id)}`)
          .sort()
          .join(',')})`
      ])
    );
  }
  return [...labels.values()].sort().join('|');
}

/** #839 -- the members with repeats removed (same fragmentKey), first occurrence kept, in order. */
export function uniqueMembers(members: readonly MarkushFragment[]): readonly MarkushFragment[] {
  const seen = new Set<string>();
  return members.filter((member) => {
    const key = fragmentKey(member);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
